import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject, OnDestroy, PLATFORM_ID, signal } from '@angular/core';
import { environment } from '@environment/environment';
import { CurrentUserService } from '@services/auth/current-user.service';
import { Subscription, timer } from 'rxjs';

export interface ProviderBalance {
	id: number;
	name: string;
	balance: number | null;
	currency: string | null;
}

/** Below this the balance chip turns red */
export const LOW_PROVIDER_BALANCE = 20;

/** Orders placed by clients also spend balance: refresh every hour while the tab is visible (served from the backend cache) */
const POLL_INTERVAL_MS = 60 * 60 * 1000;

/** A failed read (e.g. backend restarting) is retried soon instead of waiting for the next hour */
const RETRY_MS = 30_000;

/**
 * Balance on the providers' panels (admins only), shown in the topbar.
 * Refreshed after every order the panel sends to a provider and once an hour.
 */
@Injectable({
	providedIn: 'root',
})
export class ProviderBalanceService implements OnDestroy {
	private http = inject(HttpClient);
	private currentUserService = inject(CurrentUserService);
	private platformId = inject(PLATFORM_ID);

	readonly balances = signal<ProviderBalance[]>([]);

	private pollSubscription: Subscription | null = null;
	private retryTimer: ReturnType<typeof setTimeout> | null = null;
	private lastLoadedAt = 0;

	get canView(): boolean {
		return this.currentUserService.hasPermission('orders:view-all');
	}

	/**
	 * Starts the polling (idempotent); the first tick loads the balance. Safe to call every time the
	 * topbar is created (it is recreated when the layout changes): an already running poll is reused.
	 */
	start(): void {
		if (!isPlatformBrowser(this.platformId)) return;
		if (this.pollSubscription) {
			if (this.balances().length === 0) this.refresh(); // e.g. another admin logged in
			return;
		}
		this.pollSubscription = timer(0, POLL_INTERVAL_MS).subscribe(() => {
			if (document.visibilityState === 'visible') this.refresh();
		});
		// A hidden tab skips its ticks: catch up when it comes back if the balance is older than the interval
		document.addEventListener('visibilitychange', this.onVisible);
	}

	private onVisible = (): void => {
		if (document.visibilityState === 'visible' && Date.now() - this.lastLoadedAt > POLL_INTERVAL_MS) {
			this.refresh();
		}
	};

	stop(): void {
		this.pollSubscription?.unsubscribe();
		this.pollSubscription = null;
		if (isPlatformBrowser(this.platformId)) document.removeEventListener('visibilitychange', this.onVisible);
		if (this.retryTimer) clearTimeout(this.retryTimer);
		this.retryTimer = null;
		this.balances.set([]);
	}

	refresh(): void {
		if (!this.canView) {
			this.balances.set([]); // logged out or not an admin: never show someone else's balance
			return;
		}
		this.http.get<ProviderBalance[]>(`${environment.pathBackend}/providers/balance`).subscribe({
			next: (balances) => {
				this.balances.set(balances);
				this.lastLoadedAt = Date.now();
			},
			error: () => {
				// Keep the last known balance and try again shortly
				if (this.pollSubscription && !this.retryTimer) {
					this.retryTimer = setTimeout(() => {
						this.retryTimer = null;
						this.refresh();
					}, RETRY_MS);
				}
			},
		});
	}

	ngOnDestroy(): void {
		this.stop();
	}
}
